import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows, GridColumns } from '@visx/grid';
import { Plus, Check, X } from 'lucide-react';

import { DayPhotoStrip, type DayStripPhoto } from '@/components/report/day/DayPhotoStrip';
import { fmtDayTime, makeTimeAxis, spansDays } from '@/components/report/day/timeAxis';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useT } from '@/contexts/I18nContext';
import type {
  ChartMode,
  ChartSeries,
  ChartPhoto,
  ChartSegment,
  ChartKeypoint,
  Domain,
} from './types';
import {
  clamp,
  clampDomain,
  timeToPx,
  pxToTime,
  pinchDomain,
  panByPixels,
  marqueeDomain,
  zoomAtPixel,
  valueAt,
  MIN_SPAN_MS,
} from './chartMath';
import { ModeKeypad } from './ModeKeypad';

// ── layout ──
const MARGIN = { top: 8, right: 14, bottom: 22, left: 40 };
const DRAG_MIN_PX = 8; // below this a pointer-up is a tap (DayChartLightbox parity)
const DOUBLE_TAP_MS = 320; // DayChartLightbox parity
const HANDLE_R = 5; // EditHandles parity
const HIT_PX = 14; // pin / keypoint grab tolerance (finger-friendly)
const ACCENT = 'var(--color-primary)';
// Photo strip sizing (items 1-3) — same values DayRacksCard already tuned for
// rack crops (nominal width close enough to the real aspect that the
// pre-load→loaded resize is barely visible, and wide screens still fit ≥10).
const PHOTO_SLOT_W = 180;
const PHOTO_IMG_H = 130;

const fmtClock = (d: Date) => d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

export interface AnnotatableTimeChartProps {
  series: ChartSeries[];
  photos?: ChartPhoto[];
  domainFull: Domain;
  height?: number;
  title?: string;

  /** Controlled mode. Omit for uncontrolled (starts at `defaultMode`). */
  mode?: ChartMode;
  defaultMode?: ChartMode;
  onModeChange?: (m: ChartMode) => void;

  /** What an empty-space drag in annotate mode creates. F1/F3 → 'segment';
   *  F2 (keypoints via chips, only dragged here) → 'none'. */
  annotateCreates?: 'segment' | 'none';

  segments?: ChartSegment[];
  keypoints?: ChartKeypoint[];

  /** Controlled selection (segment or keypoint id). Omit for uncontrolled. */
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;

  onSegmentCreate?: (seg: ChartSegment) => void;
  onSegmentChange?: (seg: ChartSegment) => void;
  onKeypointChange?: (kp: ChartKeypoint) => void;

  /** Editor's readout, rendered below the chart. */
  readout?: React.ReactNode;
  /** Dense fresh frames for the current zoom window (offline prefetch, item G). */
  loadWindowPhotos?: (d: Domain) => Promise<ChartPhoto[]>;
  /** DESKTOP explicit "Добавить проблему" button label (Alexey correction,
   *  2026-09): on desktop, creating a segment is a deliberate click, not a
   *  drag-on-empty-space gesture — that gesture is now reserved for
   *  marquee-zoom (the chart's only desktop mode). Pass to show the button;
   *  omit to hide it (e.g. F2 keypoints, which are chip-added by the editor
   *  itself, not via this generic button). Mobile is UNCHANGED — it still
   *  drag-creates in annotate mode alongside the mode keypad. Round-2 item 6:
   *  now rendered in the BOTTOM control bar (default/nothing-selected state),
   *  not the top header — "как на соседних графиках". */
  addSegmentLabel?: string;

  /** Round-2 item 14: force the left axis to a fixed domain (e.g. `[0, 100]`
   *  for a %-fullness chart) instead of autoscaling to the data max — a
   *  STACKED series' cumulative top can exceed 100, which must never stretch
   *  the axis past 100%. Tooltip values stay each series' own (unstacked)
   *  reading regardless (see `ChartSeries.baseline`). */
  yDomain?: [number, number];
  /** Round-2 item 14: unit suffix appended to axis ticks + tooltip values
   *  (e.g. '%') — shown everywhere a value is shown, not just some places. */
  valueUnit?: string;
}

// Small controllable-state helper (controlled prop wins; else internal state).
function useControllable<T>(controlled: T | undefined, initial: T): [T, (v: T) => void] {
  const [internal, setInternal] = useState<T>(initial);
  const value = controlled !== undefined ? controlled : internal;
  return [value, setInternal];
}

type Gesture =
  | { type: 'idle' }
  | { type: 'pan'; lastX: number; moved: boolean }
  | { type: 'marquee'; startX: number; curX: number }
  | { type: 'createSeg'; startT: number; curT: number }
  | { type: 'dragBound'; id: string; edge: 'start' | 'end' }
  | { type: 'dragKp'; id: string; moved: boolean }
  | { type: 'tap'; startX: number; startY: number };

export function AnnotatableTimeChart({
  series,
  photos = [],
  domainFull,
  height = 300,
  title,
  mode: modeProp,
  defaultMode = 'annotate',
  onModeChange,
  annotateCreates = 'segment',
  segments = [],
  keypoints = [],
  selectedId: selectedIdProp,
  onSelect,
  onSegmentCreate,
  onSegmentChange,
  onKeypointChange,
  readout,
  loadWindowPhotos,
  addSegmentLabel,
  yDomain,
  valueUnit,
}: AnnotatableTimeChartProps) {
  const { t } = useT();
  // Desktop: marquee-zoom is the ONLY mode (no pan, no mode picker) — see the
  // 2026-09 correction in docs/report-validation-ui/TASK.md §E. Mobile keeps
  // the full annotate/zoom/pan picker + drag-to-create exactly as before.
  const mobile = useMediaQuery('(max-width: 767px)');
  const [mode, setModeInternal] = useControllable(modeProp, defaultMode);
  const [selectedId, setSelectedInternal] = useControllable<string | null>(selectedIdProp, null);
  const [domain, setDomain] = useState<Domain>(domainFull);
  const [tempMode, setTempMode] = useState<ChartMode | null>(null);
  const [hovered, setHovered] = useState(false);
  // Hover tooltip position (plot px), tracked independently of the gesture
  // state machine below — only shown while idle (no drag/pinch in progress).
  const [hoverPx, setHoverPx] = useState<number | null>(null);

  // Keep the zoom domain valid if the full day changes (e.g. new report).
  useEffect(() => {
    setDomain((d) => clampDomain(d, domainFull));
  }, [domainFull[0], domainFull[1]]);

  const setMode = useCallback(
    (m: ChartMode) => {
      setModeInternal(m);
      onModeChange?.(m);
    },
    [onModeChange, setModeInternal],
  );
  const select = useCallback(
    (id: string | null) => {
      setSelectedInternal(id);
      onSelect?.(id);
    },
    [onSelect, setSelectedInternal],
  );

  // Round-2 item 5: "Отмена"/click-elsewhere reverts whatever bound/value
  // edits were made DURING this selection session, back to how the segment
  // or keypoint looked the moment it got selected — then deselects. Take the
  // snapshot once per fresh selection (not on every edit inside it).
  const selectionSnapshotRef = useRef<{ id: string; seg?: ChartSegment; kp?: ChartKeypoint } | null>(null);
  useEffect(() => {
    if (!selectedId) {
      selectionSnapshotRef.current = null;
      return;
    }
    if (selectionSnapshotRef.current?.id === selectedId) return;
    const seg = segments.find((s) => s.id === selectedId);
    const kp = keypoints.find((k) => k.id === selectedId);
    selectionSnapshotRef.current = { id: selectedId, seg: seg ? { ...seg } : undefined, kp: kp ? { ...kp } : undefined };
    // deliberately depends only on `selectedId` — re-snapshot on a SELECTION
    // change, not on every edit made within it (segments/keypoints excluded).
  }, [selectedId]);
  const cancelSelection = useCallback(() => {
    const snap = selectionSnapshotRef.current;
    if (snap && snap.id === selectedId) {
      if (snap.seg && onSegmentChange) onSegmentChange(snap.seg);
      if (snap.kp && onKeypointChange) onKeypointChange(snap.kp);
    }
    select(null);
  }, [selectedId, onSegmentChange, onKeypointChange, select]);

  // Desktop ignores the mode state entirely — always marquee-zoom. Mobile
  // keeps the full picker + shift/ctrl temp-mode behaviour.
  const effectiveMode: ChartMode = mobile ? (tempMode ?? mode) : 'zoom';
  const zoomed = domain[0] > domainFull[0] || domain[1] < domainFull[1];

  // ── width measurement ──
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 1);
  const plotH = Math.max(height - MARGIN.top - MARGIN.bottom, 1);

  // ── scales ──
  const xScale = useMemo(
    () => scaleTime({ domain: [new Date(domain[0]), new Date(domain[1])], range: [0, plotW] }),
    [domain[0], domain[1], plotW],
  );
  // Shared time axis (timeAxis.ts): ticks + labels follow the VISIBLE (zoomed)
  // domain — across midnight → «пн 20.10» / «20.10 14:00», one day → clock (#2646).
  const xAxis = useMemo(() => makeTimeAxis([domain[0], domain[1]], 6), [domain[0], domain[1]]);
  const axes = useMemo(() => {
    const maxOf = (a: 'left' | 'right') => {
      let m = 0;
      for (const s of series) if (s.axis === a) for (const p of s.points) if (p.v > m) m = p.v;
      return m;
    };
    const mk = (a: 'left' | 'right') => {
      // item 14: a fixed left-axis domain (e.g. [0,100] for %-fullness charts)
      // overrides autoscale — a STACKED series' cumulative top can exceed
      // 100, which must never stretch the axis past the fixed 0..100% scale.
      if (a === 'left' && yDomain) {
        return scaleLinear({ domain: yDomain, range: [plotH, 0] });
      }
      const raw = maxOf(a);
      const max = raw <= 100 ? Math.max(raw, 1) : raw * 1.1;
      return scaleLinear({ domain: [0, max || 1], range: [plotH, 0], nice: true });
    };
    return { left: mk('left'), right: mk('right') };
  }, [series, plotH, yDomain?.[0], yDomain?.[1]]);
  const hasRight = series.some((s) => s.axis === 'right');

  // ── temp-mode via shift(zoom)/ctrl(pan), only while pointer is over ──
  // Mobile only — desktop has a single mode (marquee-zoom), no toggle.
  useEffect(() => {
    if (!hovered || !mobile) {
      setTempMode(null);
      return;
    }
    const evalMods = (e: KeyboardEvent) => {
      if (e.shiftKey) setTempMode('zoom');
      else if (e.ctrlKey || e.metaKey) setTempMode('pan');
      else setTempMode(null);
    };
    window.addEventListener('keydown', evalMods);
    window.addEventListener('keyup', evalMods);
    return () => {
      window.removeEventListener('keydown', evalMods);
      window.removeEventListener('keyup', evalMods);
    };
  }, [hovered, mobile]);

  // ── pointer/gesture plumbing ──
  const overlayRef = useRef<SVGRectElement | null>(null);
  const gestureRef = useRef<Gesture>({ type: 'idle' });
  const pointersRef = useRef<Map<number, { x: number }>>(new Map());
  const pinchRef = useRef<{ anchorA: number; anchorB: number; idA: number; idB: number } | null>(
    null,
  );
  const lastTapRef = useRef(0);
  const [, forceRender] = useState(0);
  const bump = () => forceRender((n) => n + 1);

  // clientX → plot px (0..plotW), and → time.
  const plotXOf = useCallback((clientX: number) => {
    const el = overlayRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return clamp(clientX - r.left, 0, plotW);
  }, [plotW]);
  const timeOf = useCallback(
    (clientX: number) => pxToTime(plotXOf(clientX), domain, plotW),
    [plotXOf, domain, plotW],
  );

  const pxOfTime = useCallback((t: number) => timeToPx(t, domain, plotW), [domain, plotW]);
  // item 9: keypoints/bound-pins sitting AT (or past, if zoomed) the domain
  // edge render clamped to the plot boundary instead of clipping in half —
  // hit-testing uses the SAME clamped position so a click on the
  // visually-pinned edge marker actually lands on it.
  const clampedPxOfTime = useCallback((t: number) => clamp(pxOfTime(t), 0, plotW), [pxOfTime, plotW]);

  // hit-test the selected segment's bounds / any keypoint at plot-x.
  const hitTest = useCallback(
    (px: number): Gesture | null => {
      const sel = segments.find((s) => s.id === selectedId);
      if (sel) {
        if (Math.abs(clampedPxOfTime(sel.t_start) - px) <= HIT_PX)
          return { type: 'dragBound', id: sel.id, edge: 'start' };
        if (Math.abs(clampedPxOfTime(sel.t_end) - px) <= HIT_PX)
          return { type: 'dragBound', id: sel.id, edge: 'end' };
      }
      // keypoints (nearest within tolerance)
      let best: { id: string; d: number } | null = null;
      for (const k of keypoints) {
        const d = Math.abs(clampedPxOfTime(k.t) - px);
        if (d <= HIT_PX && (!best || d < best.d)) best = { id: k.id, d };
      }
      if (best) return { type: 'dragKp', id: best.id, moved: false };
      // segment bodies (select on down)
      for (const s of segments) {
        if (px >= pxOfTime(s.t_start) && px <= pxOfTime(s.t_end))
          return { type: 'tap', startX: px, startY: 0 }; // resolved to select on up if not a drag
      }
      return null;
    },
    [segments, keypoints, selectedId, pxOfTime, clampedPxOfTime],
  );

  const beginPinch = useCallback(() => {
    const ids = [...pointersRef.current.keys()];
    if (ids.length < 2) return;
    const [idA, idB] = ids;
    const a = pointersRef.current.get(idA)!;
    const b = pointersRef.current.get(idB)!;
    pinchRef.current = {
      idA,
      idB,
      anchorA: pxToTime(a.x, domain, plotW),
      anchorB: pxToTime(b.x, domain, plotW),
    };
    gestureRef.current = { type: 'idle' }; // pinch supersedes single-pointer gesture
    bump();
  }, [domain, plotW]);

  const onPointerDown = (e: React.PointerEvent) => {
    setHoverPx(null); // hide the hover tooltip for the duration of any drag
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const px = plotXOf(e.clientX);
    pointersRef.current.set(e.pointerId, { x: px });
    if (pointersRef.current.size === 2) {
      beginPinch();
      return;
    }
    if (pointersRef.current.size > 2) return;

    // An existing bound-pin / keypoint is always grabbable, regardless of
    // mode — nudging an already-placed (often auto-prefilled) annotation is
    // a direct-manipulation action, not gated behind "annotate mode" (2026-09
    // correction: desktop no longer HAS an annotate mode). Only fresh-segment
    // creation is mode/gesture-gated below.
    const hit = hitTest(px);
    if (hit && hit.type === 'dragBound') {
      gestureRef.current = hit;
      bump();
      return;
    }
    if (hit && hit.type === 'dragKp') {
      select(hit.id);
      // item 10b: a `fixed` keypoint (open/close) is selectable — so its
      // readout/tooltip shows — but never draggable.
      const kp = keypoints.find((k) => k.id === hit.id);
      gestureRef.current = kp?.fixed ? { type: 'idle' } : hit;
      bump();
      return;
    }
    if (hit && hit.type === 'tap') {
      // segment body — select immediately, no drag.
      const seg = segments.find((s) => px >= pxOfTime(s.t_start) && px <= pxOfTime(s.t_end));
      if (seg) select(seg.id);
      gestureRef.current = { type: 'idle' };
      bump();
      return;
    }

    const m = effectiveMode;
    if (m === 'pan') {
      gestureRef.current = { type: 'pan', lastX: px, moved: false };
    } else if (m === 'zoom') {
      gestureRef.current = { type: 'marquee', startX: px, curX: px };
    } else if (annotateCreates === 'segment') {
      // Mobile-only path now (desktop's effectiveMode is always 'zoom' —
      // see the 2026-09 correction; desktop creates via the explicit
      // "Добавить проблему" button instead).
      gestureRef.current = { type: 'createSeg', startT: timeOf(e.clientX), curT: timeOf(e.clientX) };
    } else {
      gestureRef.current = { type: 'tap', startX: px, startY: e.clientY };
    }
    bump();
  };

  const onPointerMove = (e: React.PointerEvent) => {
    // Hover tooltip position — tracked whenever idle, independent of whether
    // this pointer has an active drag registered (a plain hover never calls
    // onPointerDown, so it'd never reach the gesture code below otherwise).
    if (gestureRef.current.type === 'idle' && !pinchRef.current) {
      setHoverPx(plotXOf(e.clientX));
    }

    const p = pointersRef.current.get(e.pointerId);
    if (!p) return;
    const px = plotXOf(e.clientX);
    p.x = px;

    // Pinch: recompute domain to keep both anchors under both fingers.
    if (pinchRef.current) {
      const { idA, idB, anchorA, anchorB } = pinchRef.current;
      const a = pointersRef.current.get(idA);
      const b = pointersRef.current.get(idB);
      if (a && b) {
        const next = pinchDomain(anchorA, anchorB, a.x, b.x, plotW);
        if (next) setDomain(clampDomain(next, domainFull));
      }
      return;
    }

    const g = gestureRef.current;
    switch (g.type) {
      case 'pan': {
        const dPx = px - g.lastX;
        if (Math.abs(dPx) > 0) {
          setDomain((d) => clampDomain(panByPixels(d, dPx, plotW), domainFull));
          gestureRef.current = { type: 'pan', lastX: px, moved: true };
        }
        break;
      }
      case 'marquee':
        gestureRef.current = { ...g, curX: px };
        bump();
        break;
      case 'createSeg':
        gestureRef.current = { ...g, curT: timeOf(e.clientX) };
        bump();
        break;
      case 'dragBound': {
        const seg = segments.find((s) => s.id === g.id);
        if (seg && onSegmentChange) {
          const tDrag = timeOf(e.clientX);
          const next =
            g.edge === 'start'
              ? { ...seg, t_start: Math.min(tDrag, seg.t_end - MIN_SPAN_MS) }
              : { ...seg, t_end: Math.max(tDrag, seg.t_start + MIN_SPAN_MS) };
          onSegmentChange(next);
        }
        break;
      }
      case 'dragKp': {
        const kp = keypoints.find((k) => k.id === g.id);
        if (kp && onKeypointChange) {
          onKeypointChange({ ...kp, t: clamp(timeOf(e.clientX), domainFull[0], domainFull[1]) });
          gestureRef.current = { ...g, moved: true };
        }
        break;
      }
      default:
        break;
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    pointersRef.current.delete(e.pointerId);
    // Leaving pinch — if one finger remains, stop domain-jumping (don't restart
    // a single gesture mid-flight; wait for a fresh down).
    if (pinchRef.current) {
      if (pointersRef.current.size < 2) {
        pinchRef.current = null;
        gestureRef.current = { type: 'idle' };
        bump();
      }
      return;
    }

    const g = gestureRef.current;
    const px = plotXOf(e.clientX);
    if (g.type === 'marquee') {
      const dx = Math.abs(px - g.startX);
      if (dx < DRAG_MIN_PX) {
        // item 5: a plain click on empty space (not a real marquee-drag) —
        // this is desktop's "click elsewhere" path (desktop's only mode IS
        // marquee-zoom, so a non-drag click lands here, not the 'tap' case
        // below). Was a no-op dead end when something was selected; now
        // cancels (reverts + deselects) it, same as the explicit button.
        maybeDoubleTapReset(() => cancelSelection());
      } else {
        const d = marqueeDomain(g.startX, px, domain, plotW);
        if (d[1] - d[0] >= MIN_SPAN_MS) setDomain(clampDomain(d, domainFull));
      }
    } else if (g.type === 'createSeg') {
      const lo = Math.min(g.startT, g.curT);
      const hi = Math.max(g.startT, g.curT);
      if (hi - lo >= MIN_SPAN_MS && onSegmentCreate) {
        const id = `seg-${Math.round(lo)}-${Math.round(hi)}`;
        onSegmentCreate({ id, t_start: lo, t_end: hi });
        select(id);
      } else {
        // tiny drag on empty = tap → click-elsewhere cancel (item 5).
        maybeDoubleTapReset(() => cancelSelection());
      }
    } else if (g.type === 'tap') {
      maybeDoubleTapReset(() => cancelSelection());
    } else if (g.type === 'pan' && !g.moved) {
      maybeDoubleTapReset(() => cancelSelection());
    }
    gestureRef.current = { type: 'idle' };
    bump();
  };

  // A tap that isn't a double-tap runs `onSingle`; a second tap within the
  // window resets the zoom (double-tap/double-click → whole day).
  const maybeDoubleTapReset = (onSingle?: () => void) => {
    const now = Date.now();
    if (now - lastTapRef.current < DOUBLE_TAP_MS) {
      setDomain(domainFull);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
      onSingle?.();
    }
  };

  // Wheel: ONLY zoom on a trackpad pinch (browsers synthesize wheel events
  // with ctrlKey=true for that gesture) or an explicit ctrl/cmd+scroll — a
  // PLAIN vertical scroll must scroll the PAGE, not the chart (item 13: it
  // used to always zoom, trapping the page under every chart the cursor
  // crossed). Anchored at the cursor, same math as before.
  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return; // let the page scroll normally
    e.preventDefault();
    const px = plotXOf(e.clientX);
    const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
    setDomain((d) => clampDomain(zoomAtPixel(d, px, factor, plotW), domainFull));
  };

  // item 11 (round-1): a draggable bound-pin/keypoint shows the GRAB pointer
  // (was always crosshair, even right over a handle) — hit-test the current
  // hover position the same way pointerdown does, so hovering a handle
  // previews the grab before you commit to the drag.
  const activeGesture = gestureRef.current.type;
  const hoverHit = hoverPx != null && activeGesture === 'idle' ? hitTest(hoverPx) : null;
  // Round-2 item 10b: a `fixed` keypoint (open/close) is never draggable —
  // it gets the plain pointer cursor, not grab/grabbing.
  const hoverFixedKp =
    hoverHit?.type === 'dragKp' ? keypoints.find((k) => k.id === hoverHit.id && k.fixed) : undefined;
  const overHandle =
    !hoverFixedKp &&
    (activeGesture === 'dragBound' ||
      activeGesture === 'dragKp' ||
      hoverHit?.type === 'dragBound' ||
      hoverHit?.type === 'dragKp');
  const cursor = overHandle
    ? activeGesture === 'dragBound' || activeGesture === 'dragKp'
      ? 'grabbing'
      : 'grab'
    : hoverFixedKp || hoverHit?.type === 'tap'
      ? 'pointer' // item 4/10b: a selectable-but-not-draggable target (segment body, fixed keypoint)
      : effectiveMode === 'pan'
        ? activeGesture === 'pan'
          ? 'grabbing'
          : 'grab'
        : 'crosshair'; // zoom (desktop default) and mobile annotate both marquee/crosshair-style

  // ── photo strip (re-lays-out to the current domain, like DayChartLightbox) ──
  const stripPhotos: DayStripPhoto[] = useMemo(
    () =>
      photos
        .filter((p) => p.t >= domain[0] && p.t <= domain[1])
        .map((p) => ({ image_url: p.url, at: new Date(p.t).toISOString(), metric: p.caption ?? null })),
    [photos, domain[0], domain[1]],
  );

  // dense-frame loader on zoom (item G) — best-effort, ignored if absent.
  const [, setWindowPhotos] = useState<ChartPhoto[] | null>(null);
  const loadRef = useRef(loadWindowPhotos);
  loadRef.current = loadWindowPhotos;
  useEffect(() => {
    const load = loadRef.current;
    if (!load || !zoomed) {
      setWindowPhotos(null);
      return;
    }
    let live = true;
    load(domain).then((ph) => live && setWindowPhotos(ph)).catch(() => live && setWindowPhotos(null));
    return () => {
      live = false;
    };
  }, [domain[0], domain[1], zoomed]);

  // ── hover tooltip content (time + each series' interpolated value) ──
  // item 14: a STACKED series' `points` are cumulative (the visual top of
  // its band) — the tooltip must show that series' OWN reading, i.e.
  // points-baseline at the same instant, not the stacked total.
  const hoverInfo = useMemo(() => {
    if (hoverPx == null) return null;
    const tAt = pxToTime(hoverPx, domain, plotW);
    const rows = series
      .map((s) => {
        const top = valueAt(s.points, tAt);
        const base = s.baseline ? valueAt(s.baseline, tAt) : null;
        const v = top != null ? (top - (base ?? 0)) * (s.valueScale ?? 1) : null;
        return { label: s.label, color: s.color, v };
      })
      .filter((r) => r.v != null);
    if (!rows.length) return null;
    return { t: tAt, rows };
  }, [hoverPx, domain[0], domain[1], plotW, series]);

  // ── mode-aware interaction hint (generic — the report-specific readout is
  //    the editor's job via the `readout` slot) ──
  // item 6: the add-button moved into the bottom bar itself (rendered right
  // next to this hint), so the hint no longer needs to also announce it.
  const hint = !mobile
    ? 'Выделите период, чтобы приблизить'
    : effectiveMode === 'zoom'
      ? 'Перетащите (или разведите два пальца), чтобы приблизить'
      : effectiveMode === 'pan'
        ? 'Перетащите, чтобы переместить график'
        : annotateCreates === 'segment'
          ? 'Перетащите по графику, чтобы добавить'
          : 'Перетащите точку, чтобы переместить';

  // ── render ──
  const clipId = 'atc-plot-clip';

  // Desktop-only explicit "Добавить проблему" — creates a segment centred in
  // the current visible window (a sensible default window, sized to a
  // fraction of what's on screen so it's never a hairline nor edge-to-edge)
  // and selects it for immediate bound-drag/readout refinement. Replaces
  // drag-to-create on desktop (2026-09 correction — that gesture is now
  // exclusively marquee-zoom there); mobile is unaffected.
  const addNewSegment = useCallback(() => {
    if (!onSegmentCreate) return;
    const span = domain[1] - domain[0];
    const segSpan = Math.max(MIN_SPAN_MS, Math.round(span * 0.15));
    const mid = (domain[0] + domain[1]) / 2;
    const t_start = Math.round(mid - segSpan / 2);
    const t_end = Math.round(mid + segSpan / 2);
    const id = `seg-${t_start}-${t_end}`;
    onSegmentCreate({ id, t_start, t_end });
    select(id);
  }, [domain, onSegmentCreate, select]);

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      {title && (
        <div className="mb-2 px-1 text-sm font-semibold text-foreground">{title}</div>
      )}

      {stripPhotos.length > 0 && (
        <div className="mb-2">
          <DayPhotoStrip
            photos={stripPhotos}
            workStart={new Date(domain[0]).toISOString()}
            workEnd={new Date(domain[1]).toISOString()}
            onOpen={() => {}}
            // items 1-3: the previous call left every knob at its (small,
            // 1-row, no-minimum) default — Riders Lodge «Выпечка»'s wide
            // crops fit only ~3 across before scrolling. Match DayRacksCard's
            // already-tuned values: a real nominal width (cuts the small→wide
            // resize flash on load — item 3), ≥10 photos guaranteed (item 1),
            // 2 rows so wide crops that still don't fit get a second row
            // instead of scrolling (item 1), and the strip's dots aligned to
            // this chart's actual plot area (item 2 — was unset, so the rail
            // didn't line up with the x-axis at all).
            slotW={PHOTO_SLOT_W}
            imgH={PHOTO_IMG_H}
            minCount={10}
            lanes={2}
            coverageFirst
            axisInset={{ left: MARGIN.left, right: MARGIN.right }}
          />
        </div>
      )}

      <div ref={wrapRef} className="relative w-full" style={{ touchAction: 'none' }}>
        <ModeKeypad
          mode={mode}
          onModeChange={setMode}
          zoomed={zoomed}
          onReset={() => setDomain(domainFull)}
          tempMode={tempMode}
          hideModes={!mobile}
        />
        <svg
          width={width}
          height={height}
          className="block select-none"
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => { setHovered(false); setHoverPx(null); }}
          onWheel={onWheel}
        >
          <defs>
            <clipPath id={clipId}>
              <rect x={0} y={0} width={plotW} height={plotH} />
            </clipPath>
          </defs>
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            <GridRows scale={axes.left} width={plotW} height={plotH} stroke="var(--color-border)" strokeOpacity={0.4} numTicks={4} />
            <GridColumns scale={xScale} width={plotW} height={plotH} stroke="var(--color-border)" strokeOpacity={0.25} numTicks={6} />

            {/* marks (clipped to the plot area so zoomed content stays inside) */}
            <g clipPath={`url(#${clipId})`}>
              {series.map((s) => (
                <SeriesMark
                  key={s.id}
                  s={s}
                  xOf={(t) => xScale(new Date(t)) as number}
                  yScale={s.axis === 'right' ? axes.right : axes.left}
                  plotH={plotH}
                />
              ))}

              {/* segment bodies (bound-pins render in the UNCLIPPED group below — item 9) */}
              {segments.map((seg) => {
                const x0 = xScale(new Date(seg.t_start)) as number;
                const x1 = xScale(new Date(seg.t_end)) as number;
                const isSel = seg.id === selectedId;
                return (
                  <rect
                    key={seg.id}
                    x={Math.min(x0, x1)}
                    y={0}
                    width={Math.abs(x1 - x0)}
                    height={plotH}
                    fill={ACCENT}
                    fillOpacity={isSel ? 0.22 : 0.12}
                    stroke={ACCENT}
                    strokeOpacity={isSel ? 0.9 : 0.5}
                    strokeWidth={1}
                  />
                );
              })}

              {/* in-progress create/marquee overlays */}
              {gestureRef.current.type === 'createSeg' && (
                <rect
                  x={Math.min(xScale(new Date(gestureRef.current.startT)) as number, xScale(new Date(gestureRef.current.curT)) as number)}
                  y={0}
                  width={Math.abs((xScale(new Date(gestureRef.current.curT)) as number) - (xScale(new Date(gestureRef.current.startT)) as number))}
                  height={plotH}
                  fill={ACCENT}
                  fillOpacity={0.18}
                  stroke={ACCENT}
                  strokeDasharray="3 2"
                />
              )}
              {gestureRef.current.type === 'marquee' && Math.abs(gestureRef.current.curX - gestureRef.current.startX) > 1 && (
                <rect
                  x={Math.min(gestureRef.current.startX, gestureRef.current.curX)}
                  y={0}
                  width={Math.abs(gestureRef.current.curX - gestureRef.current.startX)}
                  height={plotH}
                  fill={ACCENT}
                  fillOpacity={0.15}
                  stroke={ACCENT}
                />
              )}

              {/* hover guide (tooltip is the HTML box below the svg) */}
              {hoverInfo && (
                <line
                  x1={hoverPx!}
                  x2={hoverPx!}
                  y1={0}
                  y2={plotH}
                  stroke="var(--color-muted-foreground)"
                  strokeOpacity={0.5}
                  strokeDasharray="2 2"
                  pointerEvents="none"
                />
              )}
            </g>

            {/* item 9: bound-pins + keypoints render OUTSIDE the clip path —
                a marker sitting exactly at (or, mid-zoom, past) the domain
                edge would otherwise be sliced in half by the plot's own clip
                rect. Position is clamped to the plot box (clampedPxOfTime),
                so an off-screen-while-zoomed marker pins to the boundary
                instead of vanishing, matching the same "draw at the frame
                edge, don't hide" convention used for off-frame polygon
                points elsewhere in this app. */}
            <g>
              {segments
                .filter((seg) => seg.id === selectedId)
                .flatMap((seg) => [
                  { t: seg.t_start, key: `${seg.id}-s` },
                  { t: seg.t_end, key: `${seg.id}-e` },
                ])
                .map(({ t, key }) => (
                  <Pin key={key} x={clampedPxOfTime(t)} plotH={plotH} />
                ))}
              {keypoints.map((k) => (
                <Pin
                  key={k.id}
                  x={clampedPxOfTime(k.t)}
                  plotH={plotH}
                  filled={k.id === selectedId}
                  head
                  color={k.color}
                  fixed={k.fixed}
                  level={k.level}
                  yScale={axes.left}
                />
              ))}
            </g>

            <AxisBottom top={plotH} scale={xScale} tickValues={xAxis.ticks?.map((ms) => new Date(ms))} numTicks={6} tickFormat={(v) => xAxis.format(v)} stroke="var(--color-border)" tickStroke="var(--color-border)" tickLabelProps={() => ({ fill: 'var(--color-muted-foreground)', fontSize: 10, textAnchor: 'middle', dy: '0.25em' })} />
            <AxisLeft scale={axes.left} numTicks={4} stroke="var(--color-border)" tickStroke="var(--color-border)" tickLabelProps={() => ({ fill: 'var(--color-muted-foreground)', fontSize: 10, textAnchor: 'end', dx: '-0.25em', dy: '0.25em' })} tickFormat={(v) => `${v}${valueUnit ?? ''}`} />
            {hasRight && (
              <AxisLeft left={plotW} scale={axes.right} numTicks={4} stroke="var(--color-border)" tickStroke="var(--color-border)" tickLabelProps={() => ({ fill: 'var(--color-muted-foreground)', fontSize: 10, textAnchor: 'start', dx: '0.35em', dy: '0.25em' })} />
            )}

            {/* interaction overlay — captures all pointer events for the plot. */}
            <rect
              ref={overlayRef}
              x={0}
              y={0}
              width={plotW}
              height={plotH}
              fill="transparent"
              style={{ cursor, touchAction: 'none' }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endPointer}
              onPointerCancel={endPointer}
            />
          </g>
        </svg>

        {hoverInfo && (
          <div
            className="pointer-events-none absolute z-20 flex flex-col gap-0.5 rounded-md border border-border bg-popover/95 px-2 py-1 text-[11px] shadow-md backdrop-blur"
            style={{
              left: clamp(MARGIN.left + hoverPx!, 4, width - 4),
              top: MARGIN.top + 4,
              transform: hoverPx! > plotW / 2 ? 'translateX(-100%)' : undefined,
            }}
          >
            <div className="font-medium text-foreground">{spansDays(domain[0], domain[1]) ? fmtDayTime(hoverInfo.t) : fmtClock(new Date(hoverInfo.t))}</div>
            {hoverInfo.rows.map((r) => (
              <div key={r.label} className="flex items-center gap-1.5 text-muted-foreground">
                <span className="inline-block size-2 rounded-full" style={{ background: r.color }} />
                {r.label}: <span className="font-medium text-foreground">{Math.round(r.v!)}{valueUnit ?? ''}</span>
              </div>
            ))}
          </div>
        )}

        {/* item 10b: fast custom tooltip for a `fixed` keypoint — explains
            why it won't drag. NOT the native `title=` attribute (slow to
            appear); shown immediately once hover resolves onto it, same as
            the value tooltip above. */}
        {hoverFixedKp && !hoverInfo && (
          <div
            className="pointer-events-none absolute z-20 max-w-[220px] rounded-md border border-border bg-popover/95 px-2 py-1 text-[11px] text-foreground shadow-md backdrop-blur"
            style={{
              left: clamp(MARGIN.left + clampedPxOfTime(hoverFixedKp.t), 4, width - 4),
              top: MARGIN.top + 4,
            }}
          >
            {hoverFixedKp.fixedTooltip ?? 'Нельзя двигать — время фиксировано'}
          </div>
        )}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 px-1">
        <span className="text-[11px] text-muted-foreground">{hint}</span>
        {/* item 6: "Добавить проблему"/"Добавить событие" lives in the bottom
            bar's default (nothing-selected) state now, matching how every
            chart offers its add-action — was a top-header button before. */}
        {!selectedId && !mobile && addSegmentLabel && onSegmentCreate && (
          <button
            type="button"
            onClick={addNewSegment}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground transition hover:border-primary hover:text-primary"
          >
            <Plus className="size-3" /> {addSegmentLabel}
          </button>
        )}
      </div>

      {readout && (
        <div className="mt-2 flex flex-col gap-2">
          {readout}
          {/* item 5: explicit Save/Cancel once something is selected — was a
              dead end (no way out of a selection except this same click-
              elsewhere gesture, which used to no-op on desktop). Cancel
              reverts to the state at selection-time (see cancelSelection). */}
          {selectedId && (onSegmentChange || onKeypointChange) && (
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelSelection}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" /> {t('reportValidation.interactive.cancelEdit')}
              </button>
              <button
                type="button"
                onClick={() => select(null)}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground transition hover:opacity-90"
              >
                <Check className="size-3.5" /> {t('reportValidation.interactive.saveEdit')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// One timeseries mark. area = filled line, step = stepped line, bar = columns.
function SeriesMark({
  s,
  xOf,
  yScale,
  plotH,
}: {
  s: ChartSeries;
  xOf: (t: number) => number;
  yScale: (v: number) => number;
  plotH: number;
}) {
  if (s.points.length === 0) return null;
  if (s.kind === 'bar') {
    const bw = s.bucketMs ? Math.max(xOf(s.points[0].t + s.bucketMs) - xOf(s.points[0].t) - 1, 1) : 4;
    return (
      <g>
        {s.points.map((p, i) => {
          const y = yScale(p.v);
          return (
            <rect
              key={i}
              x={xOf(p.t) - bw / 2}
              y={y}
              width={bw}
              height={Math.max(plotH - y, 0)}
              fill={s.color}
              fillOpacity={0.8}
              rx={1}
            />
          );
        })}
      </g>
    );
  }
  // line-ish paths
  let d = '';
  s.points.forEach((p, i) => {
    const x = xOf(p.t);
    const y = yScale(p.v);
    if (i === 0) d += `M${x},${y}`;
    else if (s.kind === 'step') d += `H${x}V${y}`;
    else d += `L${x},${y}`;
  });
  if (s.kind === 'area') {
    const first = xOf(s.points[0].t);
    const last = xOf(s.points[s.points.length - 1].t);
    // item 4: a `baseline` (same length/order as `points`) stacks this area
    // on top of whatever's below it instead of always dropping to the
    // plot floor — several stacked `area` series read as one stacked-area
    // chart, matching the "normal"/конструктор mode elsewhere in the app.
    let areaD: string;
    if (s.baseline && s.baseline.length === s.points.length) {
      let low = '';
      for (let i = s.baseline.length - 1; i >= 0; i -= 1) {
        const p = s.baseline[i];
        low += `L${xOf(p.t)},${yScale(p.v)}`;
      }
      areaD = `${d}${low}Z`;
    } else {
      areaD = `${d}L${last},${plotH}L${first},${plotH}Z`;
    }
    return (
      <g>
        <path d={areaD} fill={s.color} fillOpacity={s.baseline ? 0.55 : 0.15} />
        <path d={d} fill="none" stroke={s.color} strokeWidth={1.25} />
      </g>
    );
  }
  return <path d={d} fill="none" stroke={s.color} strokeWidth={1.5} strokeOpacity={0.85} />;
}

// Draggable handle — EditHandles convention ported to SVG: unselected = white
// fill + accent ring; selected = accent fill + white ring; r=5, no shadow. A
// vertical guide line runs down the plot so the operator sees the bound.
function Pin({
  x,
  plotH,
  filled = true,
  head = false,
  color,
  fixed = false,
  level,
  yScale,
}: {
  x: number;
  plotH: number;
  filled?: boolean;
  head?: boolean;
  /** items 15-19: colour-link this pin to its own report-line card. */
  color?: string;
  /** item 10a/b: never-draggable (open/close keypoints) — rendered as a
   *  square instead of a circle, bigger/bolder so it reads as "different"
   *  at a glance (also item 10a: markers in general were too easy to miss). */
  fixed?: boolean;
  /** item 10e: the currently-selected slider LEVEL (0..100, left-axis scale)
   *  — drawn as a small tick alongside the pin, so both WHEN and the chosen
   *  VALUE are visible without opening the readout. */
  level?: number;
  yScale?: (v: number) => number;
}) {
  const c = color ?? ACCENT;
  const r = fixed ? HANDLE_R + 1 : HANDLE_R;
  const cy = head ? 8 : 0;
  return (
    <g pointerEvents="none">
      <line
        x1={x}
        y1={0}
        x2={x}
        y2={plotH}
        stroke={c}
        strokeWidth={fixed ? 1.5 : 1}
        strokeOpacity={fixed ? 0.8 : 0.6}
        strokeDasharray={head ? undefined : '2 2'}
      />
      {fixed ? (
        <rect
          data-testid="fixed-marker"
          x={x - r}
          y={cy - r}
          width={r * 2}
          height={r * 2}
          fill={filled ? c : '#ffffff'}
          stroke={filled ? '#ffffff' : c}
          strokeWidth={1.5}
        />
      ) : (
        <circle cx={x} cy={cy} r={r} fill={filled ? c : '#ffffff'} stroke={filled ? '#ffffff' : c} strokeWidth={1.5} />
      )}
      {/* item 10e: level tick — a short horizontal dash at the marker's
          currently-selected value, on the left-axis scale. */}
      {level != null && yScale && (
        <line x1={x - 7} y1={yScale(level)} x2={x + 7} y2={yScale(level)} stroke={c} strokeWidth={2.5} strokeLinecap="round" />
      )}
    </g>
  );
}
